import React, { useRef } from 'react';
import { AeroPreviewTile, AeroStatus } from '../../components/Aero/AeroHomeParts';
import AeroIcon from '../../components/Aero/AeroIcon';
import { daysLeftLabel } from '../../utils/siteAccess';

/**
 * One site in the Sites list (AeroSites), as a v3 site block (m3): a preview
 * on top, then the name and its status, the address and when it last
 * changed. A view: the page passes the site, its address, its state and the
 * ⋯ menu. Until cached previews ship (product work) the preview is the site's
 * favicon, or its first letter, on the Publish wash.
 *
 * Rename happens in place (phase 7, PLAN's "inline Name setting"): with
 * `rename` ({ value, busy, onChange, onSave, onCancel }) the name becomes a
 * field. Enter or leaving the field saves, Escape cancels; leaving it
 * unchanged cancels, so nothing is written.
 */
export default function SiteCard({ site, host, isLive, access = null, updatedLabel, duplicating = false, onOpen, menu = null, rename = null }) {
  const name = site.title || site.generatedUrl;
  // `access` ({ state, daysLeft }, utils/siteAccess.js) says what visitors see.
  // A live site on an ended Free plan is offline: say so, and say how to fix it.
  const offline = isLive && access?.state === 'expired';
  const endingSoon = isLive && access?.state === 'trial' && access.daysLeft !== null && access.daysLeft <= 2;
  // Set once Enter or Escape has decided, so a blur as the field closes
  // cannot save a second time (or save after Escape).
  const settled = useRef(false);
  if (!rename) settled.current = false;
  return (
    <div
      className="aero-scard"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="aero-scard-pv">
        <AeroPreviewTile
          step="publish"
          name={name}
          favicon={site.faviconUrl || null}
          previewUrl={site.previewUrl || null}
          previewColors={site.previewColors || null}
        />
        {menu && <div className="aero-scard-menu">{menu}</div>}
      </div>
      <div className="aero-scard-meta">
        <span className="aero-scard-nm">
          {rename ? (
            <input
              className="aero-scard-rename"
              type="text"
              aria-label="Site name"
              value={rename.value}
              disabled={rename.busy}
              autoFocus={rename.autoFocus !== false}
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => { settled.current = false; rename.onChange(e.target.value); }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') { settled.current = true; rename.onSave(); }
                if (e.key === 'Escape') { settled.current = true; rename.onCancel(); }
              }}
              onBlur={() => {
                if (rename.busy || settled.current) return;
                if (!rename.value.trim() || rename.value.trim() === name) rename.onCancel();
                else rename.onSave();
              }}
            />
          ) : (
            <b>{name}</b>
          )}
          {site.shared && <span className="aero-smp" title="Shared with you by an admin">Shared</span>}
        </span>
        {offline
          ? <AeroStatus kind="off">Offline: free plan ended</AeroStatus>
          : <AeroStatus kind={isLive ? 'live' : 'off'} />}
        {offline ? (
          <span className="aero-scard-addr is-off">Upgrade to put it back online</span>
        ) : isLive ? (
          <a
            href={`https://${host}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="aero-scard-addr"
          >
            {host}
          </a>
        ) : (
          <span className="aero-scard-addr is-off">Drop a folder or .zip to publish</span>
        )}
        <span className="aero-scard-foot">
          <span>{duplicating ? 'Duplicating…' : `Updated ${updatedLabel}`}</span>
          {endingSoon && <span className="aero-scard-soon">Free plan: {daysLeftLabel(access.daysLeft)}</span>}
          {site.isPasswordActive && (
            <span className="aero-scard-lock"><AeroIcon name="lock" />Password</span>
          )}
        </span>
      </div>
    </div>
  );
}
